/**
 * A single-choice field backed by a native `<select>`.
 *
 * Used wherever the value must come from a registry — country, accreditation body,
 * accreditation status. A select cannot hold a value the registry does not offer, which
 * is the point: the server rejects an unknown or deactivated entry, and the control
 * should not be able to produce one in the first place.
 *
 * No caret to restore — a select has no text cursor, so the §9 defect does not arise.
 */

export interface Option {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  /** Shown first, selected while nothing has been chosen. */
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = "— Select —",
  hint,
  error,
  required = false,
  disabled = false,
}: SelectFieldProps) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field">
      <label className="f" htmlFor={id}>
        {label} {required ? <b>*</b> : null}
        {hint ? <i id={`${id}-hint`}> {hint}</i> : null}
      </label>
      <select
        id={id}
        className="t"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <p className="err" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
